/**
 * PmsDashboard — Operaciones · Hoy.
 *
 * All numbers derive from pmsSelectors (single-source dataset). No fetch, no
 * side-effects, no fake polling.
 */
import React, { useMemo } from "react";
import { Link } from "react-router-dom";
import { Section } from "@/pages/Shell";
import PmsShell from "@/pages/admin/pms/PmsShell";
import {
  ArrowRight, DoorOpen, SignOut, House, Broom, Bed, Sparkle,
} from "@phosphor-icons/react";
import {
  DEMO_TODAY_ISO, DEMO_MONTH, DEMO_MONTH_LABEL, POOLS, VILLA_STATE_LABEL,
} from "@/data/pmsDemoData";
import {
  arrivalsOn, departuresOn, occupiedVillasOn, cleaningTasksOn, villaGridOn,
  todayTimeline, monthlyMetrics, channelMix, upcomingReservations, getPool,
  getChannel, getVilla, formatEUR, formatPct, villasByPool,
  reservationsForVilla,
} from "@/lib/pmsSelectors";

const STATE_STYLE = {
  AVAILABLE:     { bg: "bg-stone-50 dark:bg-stone-900",       border: "border-stone-200 dark:border-stone-800",  text: "text-stone-700 dark:text-stone-300",  dot: "bg-stone-400" },
  OCCUPIED:      { bg: "bg-emerald-50 dark:bg-emerald-950/40",border: "border-emerald-200 dark:border-emerald-900",text: "text-emerald-800 dark:text-emerald-300",dot: "bg-emerald-500" },
  ARRIVAL_TODAY: { bg: "bg-amber-50 dark:bg-amber-950/40",     border: "border-amber-300 dark:border-amber-900", text: "text-amber-800 dark:text-amber-200", dot: "bg-amber-500" },
  CLEANING:      { bg: "bg-sky-50 dark:bg-sky-950/40",         border: "border-sky-200 dark:border-sky-900",     text: "text-sky-800 dark:text-sky-300",     dot: "bg-sky-500" },
  OWNER_STAY:    { bg: "bg-stone-100 dark:bg-stone-800",       border: "border-stone-300 dark:border-stone-700", text: "text-stone-800 dark:text-stone-200", dot: "bg-stone-600 dark:bg-stone-400" },
  MAINTENANCE:   { bg: "bg-red-50 dark:bg-red-950/40",         border: "border-red-200 dark:border-red-900",     text: "text-red-800 dark:text-red-300",     dot: "bg-red-500" },
  ADMIN_BLOCK:   { bg: "bg-stone-50 dark:bg-stone-900",       border: "border-stone-300 dark:border-stone-700", text: "text-stone-700 dark:text-stone-300", dot: "bg-stone-500" },
};

export default function PmsDashboard() {
  const today = DEMO_TODAY_ISO;
  const arrivals = useMemo(() => arrivalsOn(today), [today]);
  const departures = useMemo(() => departuresOn(today), [today]);
  const occupied = useMemo(() => occupiedVillasOn(today), [today]);
  const cleaning = useMemo(() => cleaningTasksOn(today), [today]);
  const villaGrid = useMemo(() => villaGridOn(today), [today]);
  const timeline = useMemo(() => todayTimeline(today), [today]);
  const monthMetrics = useMemo(() => monthlyMetrics(DEMO_MONTH), []);
  const chMix = useMemo(() => channelMix(DEMO_MONTH), []);
  const upcoming = useMemo(() => upcomingReservations(today, 6), [today]);

  const directShare = useMemo(() => {
    const directTotal = chMix
      .filter((r) => r.channel.kind === "direct")
      .reduce((acc, r) => acc + r.revenue, 0);
    return monthMetrics.accommodationRevenue > 0
      ? directTotal / monthMetrics.accommodationRevenue : 0;
  }, [chMix, monthMetrics]);

  return (
    <PmsShell>
      <Section
        title="Operaciones · Hoy"
        subtitle={`Snapshot operativo del ${formatHumanDate(today)}. Toda la actividad de las 10 villas.`}
      >
        {/* Operational KPIs */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6" data-testid="pms-ops-kpis">
          <KpiCard label="Llegadas hoy"    value={arrivals.length}    icon={DoorOpen} tone="amber" testId="pms-kpi-arrivals" />
          <KpiCard label="Salidas hoy"     value={departures.length}  icon={SignOut}  tone="sky"   testId="pms-kpi-departures" />
          <KpiCard label="Villas ocupadas" value={`${occupied.length} / 10`} icon={House} tone="emerald" testId="pms-kpi-occupied" />
          <KpiCard label="Por limpiar"     value={cleaning.length}    icon={Broom}    tone="sky"   testId="pms-kpi-cleaning" />
        </div>

        {/* Villa state grid */}
        <SubHeading>Estado de las villas</SubHeading>
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-2 mb-6" data-testid="pms-villa-grid">
          {villaGrid.map((row) => {
            const s = STATE_STYLE[row.state] || STATE_STYLE.AVAILABLE;
            const pool = getPool(row.villa.pool_id);
            return (
              <div
                key={row.villa.villa_id}
                className={`rounded-lg border p-3 ${s.bg} ${s.border}`}
                data-testid={`pms-villa-${row.villa.villa_id}`}
                data-state={row.state}
              >
                <div className="flex items-center justify-between text-[10px] uppercase tracking-widest">
                  <span className={s.text}>{row.villa.villa_id}</span>
                  <span className="text-stone-400 dark:text-stone-500">{pool?.label.replace("Dolce ", "")}</span>
                </div>
                <div className="mt-1 font-medium text-sm text-stone-900 dark:text-stone-100 truncate">
                  {row.villa.name.replace("Villa ", "")}
                </div>
                <div className={`mt-1 inline-flex items-center gap-1.5 text-[11px] ${s.text}`}>
                  <span className={`w-1.5 h-1.5 rounded-full ${s.dot}`} />
                  {VILLA_STATE_LABEL[row.state]}
                </div>
              </div>
            );
          })}
        </div>

        {/* Today timeline */}
        <SubHeading>Timeline de hoy</SubHeading>
        {timeline.length === 0 ? (
          <div className="dv-card text-sm text-stone-500 text-center py-6 mb-6">
            Sin eventos operativos hoy.
          </div>
        ) : (
          <div className="dv-card p-0 mb-6" data-testid="pms-today-timeline">
            <ul className="divide-y divide-stone-200 dark:divide-stone-800">
              {timeline.map((ev, i) => (
                <TimelineRow key={i} ev={ev} />
              ))}
            </ul>
          </div>
        )}

        {/* Month KPIs */}
        <SubHeading>{`Rendimiento · ${DEMO_MONTH_LABEL}`}</SubHeading>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6" data-testid="pms-month-kpis">
          <KpiCard label="Ocupación"     value={formatPct(monthMetrics.occupancy)}                sub={`${monthMetrics.guestNights} / ${monthMetrics.commercialAvailability} noches`}  tone="emerald" />
          <KpiCard label="ADR"           value={formatEUR(monthMetrics.adr)}                       sub="por noche" />
          <KpiCard label="RevPAR"        value={formatEUR(monthMetrics.revpar)}                    sub="por villa · día" />
          <KpiCard label="Ingresos"      value={formatEUR(monthMetrics.accommodationRevenue)}      sub="accommodation" />
          <KpiCard label="Directo"       value={formatPct(directShare)}                            sub="% de ingresos" tone="amber" />
        </div>

        {/* Channel mix */}
        <SubHeading>Mix de canales</SubHeading>
        <div className="dv-card mb-6" data-testid="pms-channel-mix">
          <div className="space-y-2">
            {chMix.map((row) => (
              <ChannelBar key={row.channel.key} row={row} total={monthMetrics.accommodationRevenue} />
            ))}
          </div>
        </div>

        {/* Allocation summary per pool */}
        <SubHeading>Allocation · resumen por pool</SubHeading>
        <div className="grid md:grid-cols-2 gap-3 mb-6" data-testid="pms-allocation-summary">
          {POOLS.map((pool) => (
            <PoolBalanceCard key={pool.pool_id} pool={pool} />
          ))}
        </div>

        {/* Upcoming reservations */}
        <SubHeading>Próximas reservas</SubHeading>
        {upcoming.length === 0 ? (
          <div className="dv-card text-sm text-stone-500 text-center py-6">
            Sin próximas reservas.
          </div>
        ) : (
          <div className="dv-card p-0" data-testid="pms-upcoming">
            <ul className="divide-y divide-stone-200 dark:divide-stone-800">
              {upcoming.map((r) => (
                <UpcomingRow key={r.reservation_id} r={r} />
              ))}
            </ul>
          </div>
        )}
      </Section>
    </PmsShell>
  );
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function SubHeading({ children }) {
  return (
    <div className="text-[10px] uppercase tracking-widest text-stone-400 dark:text-stone-500 mb-2">
      {children}
    </div>
  );
}

const TONE_STYLE = {
  emerald: "text-emerald-700 dark:text-emerald-300",
  amber:   "text-amber-700 dark:text-amber-300",
  sky:     "text-sky-700 dark:text-sky-300",
  red:     "text-red-700 dark:text-red-300",
  stone:   "text-stone-700 dark:text-stone-200",
};

function KpiCard({ label, value, sub, icon: Icon, tone = "stone", testId }) {
  const toneClass = TONE_STYLE[tone] || TONE_STYLE.stone;
  return (
    <div className="dv-card" data-testid={testId}>
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-stone-400 dark:text-stone-500">
        {Icon && <Icon size={12} weight="bold" />}
        {label}
      </div>
      <div className={`mt-1 text-2xl font-semibold ${toneClass}`}>{value}</div>
      {sub && (
        <div className="mt-0.5 text-[11px] text-stone-500 dark:text-stone-400">{sub}</div>
      )}
    </div>
  );
}

const TIMELINE_ICON = {
  checkout: SignOut,
  cleaning: Broom,
  checkin:  DoorOpen,
};

const TIMELINE_LABEL = {
  checkout: "Checkout",
  cleaning: "Limpieza",
  checkin:  "Check-in",
};

function TimelineRow({ ev }) {
  const Icon = TIMELINE_ICON[ev.kind] || Sparkle;
  const villa = ev.villa;
  const r = ev.reservation;
  return (
    <li className="flex items-center gap-3 px-4 py-3">
      <div className="w-14 text-xs text-stone-500 dark:text-stone-400 shrink-0 tabular-nums">
        {ev.time}
      </div>
      <div className="w-8 h-8 rounded-full flex items-center justify-center bg-stone-100 dark:bg-stone-800 shrink-0">
        <Icon size={14} weight="bold" className="text-stone-700 dark:text-stone-200" />
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-sm text-stone-900 dark:text-stone-100">
          <span className="font-medium">{TIMELINE_LABEL[ev.kind]}</span>
          {" · "}
          <span>{villa?.villa_id} {villa?.name}</span>
        </div>
        {r && (
          <div className="text-[11px] text-stone-500 dark:text-stone-400 truncate">
            {r.reservation_id} · {getChannel(r.channel).label} · {r.nights} noches
          </div>
        )}
      </div>
      {r && (
        <Link
          to={`/pms/reservations/${r.reservation_id}`}
          className="text-xs text-stone-500 hover:text-stone-900 dark:hover:text-stone-100 inline-flex items-center gap-1 shrink-0"
        >
          Abrir <ArrowRight size={12} />
        </Link>
      )}
    </li>
  );
}

function ChannelBar({ row, total }) {
  const share = total > 0 ? row.revenue / total : 0;
  const pct = (share * 100).toFixed(0);
  return (
    <div>
      <div className="flex items-center justify-between text-xs text-stone-700 dark:text-stone-300">
        <span>{row.channel.label}</span>
        <span className="tabular-nums text-stone-500 dark:text-stone-400">
          {formatEUR(row.revenue)} · {pct}%
        </span>
      </div>
      <div className="mt-1 h-1.5 rounded-full bg-stone-100 dark:bg-stone-800 overflow-hidden">
        <div
          className="h-full bg-stone-800 dark:bg-stone-200"
          style={{ width: `${Math.max(2, pct)}%` }}
        />
      </div>
    </div>
  );
}

function PoolBalanceCard({ pool }) {
  const villas = villasByPool(pool.pool_id);
  const rows = villas.map((v) => {
    const nights = reservationsForVilla(v.villa_id)
      .reduce((acc, r) => acc + Math.min(r.nights, 31), 0);
    const revenue = reservationsForVilla(v.villa_id)
      .reduce((acc, r) => acc + r.accommodation_revenue_eur, 0);
    return { villa: v, nights, revenue };
  }).sort((a, b) => b.revenue - a.revenue);

  const nextRecommended = rows[rows.length - 1]?.villa; // lowest revenue → fairness-preferred next

  return (
    <div className="dv-card" data-testid={`pms-pool-${pool.pool_id}`}>
      <div className="flex items-center justify-between mb-2">
        <div className="text-sm font-semibold text-stone-900 dark:text-stone-100">
          {pool.label}
        </div>
        <div className="text-[10px] uppercase tracking-widest text-stone-400">
          {villas.length} villas
        </div>
      </div>
      <ul className="text-xs divide-y divide-stone-100 dark:divide-stone-800">
        {rows.map((row) => (
          <li key={row.villa.villa_id} className="flex items-center justify-between py-1.5">
            <span className="text-stone-700 dark:text-stone-300">
              {row.villa.villa_id} · {row.villa.name.replace("Villa ", "")}
            </span>
            <span className="tabular-nums text-stone-500 dark:text-stone-400">
              {row.nights}n · {formatEUR(row.revenue)}
            </span>
          </li>
        ))}
      </ul>
      {nextRecommended && (
        <div className="mt-3 pt-3 border-t border-stone-200 dark:border-stone-800 text-xs">
          <span className="text-stone-500 dark:text-stone-400">Siguiente recomendada · </span>
          <span className="font-medium text-stone-900 dark:text-stone-100">
            {nextRecommended.villa_id} {nextRecommended.name.replace("Villa ", "")}
          </span>
        </div>
      )}
    </div>
  );
}

function UpcomingRow({ r }) {
  const villa = getVilla(r.assigned_villa_id);
  const channel = getChannel(r.channel);
  const pool = getPool(r.pool_id);
  return (
    <li>
      <Link
        to={`/pms/reservations/${r.reservation_id}`}
        className="flex items-center gap-3 px-4 py-3 hover:bg-stone-50 dark:hover:bg-stone-900"
      >
        <div className="w-16 text-xs text-stone-500 dark:text-stone-400 shrink-0 tabular-nums">
          {r.check_in.slice(5)}
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-sm text-stone-900 dark:text-stone-100 truncate">
            {villa?.villa_id} {villa?.name} · <span className="text-stone-500 dark:text-stone-400">{pool?.label}</span>
          </div>
          <div className="text-[11px] text-stone-500 dark:text-stone-400">
            {r.reservation_id} · {channel.label} · {r.nights} noches · {r.guest?.adults || 0}A{r.guest?.children ? `+${r.guest.children}N` : ""}
          </div>
        </div>
        <div className="text-sm text-stone-900 dark:text-stone-100 tabular-nums shrink-0">
          {formatEUR(r.accommodation_revenue_eur)}
        </div>
        <Bed size={14} className="text-stone-300 dark:text-stone-600 shrink-0" />
      </Link>
    </li>
  );
}

function formatHumanDate(iso) {
  const d = new Date(iso + "T00:00:00Z");
  return d.toLocaleDateString("es-ES", {
    weekday: "long", day: "numeric", month: "long", year: "numeric",
    timeZone: "UTC",
  });
}

